import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';
import { toList } from '../lib/workflowUtils';

const BASE = '/its/tickets';

export const ticketKeys = {
    all: ['support-tickets'],
    options: () => [...ticketKeys.all, 'options'],
    lists: (orgId) => [...ticketKeys.all, 'list', orgId],
    list: (orgId, filters) => [...ticketKeys.lists(orgId), filters],
    detail: (id) => [...ticketKeys.all, 'detail', id],
    comments: (id) => [...ticketKeys.all, 'comments', id],
    history: (id) => [...ticketKeys.all, 'history', id],
    summary: (orgId) => [...ticketKeys.all, 'summary', orgId],
};

// ─── FORM OPTIONS ────────────────────────────────────────────────────────────

// All dropdown options in one call — fetched once, cached for the session.
export function useTicketOptions({ enabled = true } = {}) {
    return useQuery({
        queryKey: ticketKeys.options(),
        queryFn: () => apiHelper.getResource(`${BASE}/options/`, { suppressToast: true }),
        enabled,
        staleTime: Infinity,
        gcTime: Infinity,
        retry: false,
    });
}

// ─── READS ───────────────────────────────────────────────────────────────────

export function useSupportTickets(orgId, filters = {}) {
    return useQuery({
        queryKey: ticketKeys.list(orgId, filters),
        queryFn: () => apiHelper.getResource(`${BASE}/`, { orgId, params: filters, suppressToast: true }),
        enabled: !!orgId,
        select: toList,
        retry: false,
    });
}

export function useSupportTicket(id, orgId) {
    return useQuery({
        queryKey: ticketKeys.detail(id),
        queryFn: () => apiHelper.getResource(`${BASE}/${id}/`, { orgId }),
        enabled: !!orgId && !!id,
        retry: false,
    });
}

export function useTicketSummary(orgId, { enabled = true } = {}) {
    return useQuery({
        queryKey: ticketKeys.summary(orgId),
        queryFn: () => apiHelper.getResource(`${BASE}/summary/`, { orgId, suppressToast: true }),
        enabled: !!orgId && enabled,
        staleTime: 30000,
        retry: false,
    });
}

// Server already filters internal notes for non-agents.
export function useTicketComments(id, orgId) {
    return useQuery({
        queryKey: ticketKeys.comments(id),
        queryFn: () => apiHelper.getResource(`${BASE}/${id}/comments/`, { orgId }),
        enabled: !!orgId && !!id,
        select: toList,
        retry: false,
    });
}

export function useTicketHistory(id, orgId) {
    return useQuery({
        queryKey: ticketKeys.history(id),
        queryFn: () => apiHelper.getResource(`${BASE}/${id}/history/`, { orgId, suppressToast: true }),
        enabled: !!orgId && !!id,
        select: toList,
        retry: false,
    });
}

// ─── CREATE / EDIT ───────────────────────────────────────────────────────────

// Body becomes multipart once there is at least one attachment.
function buildTicketPayload(data, attachments = []) {
    if (!attachments.length) return data;

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
        }
    });
    attachments.forEach(file => formData.append('attachments', file));
    return formData;
}

export function useCreateTicket() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, attachments, orgId }) => apiHelper.postResource(
            `${BASE}/`,
            buildTicketPayload(data, attachments),
            { orgId, suppressToast: true },
        ),
        onSuccess: (res, { orgId }) => {
            if (res?.id) queryClient.setQueryData(ticketKeys.detail(res.id), res);
            queryClient.invalidateQueries({ queryKey: ticketKeys.lists(orgId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.summary(orgId) });
        },
    });
}

// Requester may PATCH only 4 fields while status is 'new'; agents edit freely.
export function useUpdateTicket() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data, orgId }) => apiHelper.patchResource(`${BASE}/${id}/`, data, { orgId, suppressToast: true }),
        onSuccess: (res, { id, orgId }) => {
            if (res?.id) queryClient.setQueryData(ticketKeys.detail(id), res);
            queryClient.invalidateQueries({ queryKey: ticketKeys.lists(orgId) });
        },
    });
}

// ─── COMMENTS ────────────────────────────────────────────────────────────────

export function useAddTicketComment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, body, isInternal = false, orgId }) => apiHelper.postResource(
            `${BASE}/${id}/comments/`,
            { body, is_internal: isInternal },
            { orgId, suppressToast: true },
        ),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ticketKeys.comments(id) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.detail(id) });
        },
    });
}

// ─── WORKFLOW ────────────────────────────────────────────────────────────────

// Response is the full detail payload — write straight to cache.
function useTicketTransition(action) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data = {}, orgId }) => apiHelper.postResource(
            `${BASE}/${id}/${action}/`,
            data,
            { orgId, suppressToast: true },
        ),
        onSuccess: (res, { id, orgId }) => {
            if (res?.id) queryClient.setQueryData(ticketKeys.detail(id), res);
            queryClient.invalidateQueries({ queryKey: ticketKeys.lists(orgId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.summary(orgId) });
            queryClient.invalidateQueries({ queryKey: ticketKeys.history(id) });
        },
    });
}

// Needs its_triage or its_agent. assignee_id is an INTEGER user id, not a UUID.
export const useAssignTicket = () => useTicketTransition('assign');
export const useSetTicketPriority = () => useTicketTransition('set-priority');
export const useSetTicketStatus = () => useTicketTransition('status');
export const useResolveTicket = () => useTicketTransition('resolve');

// Requester or agent.
export const useCloseTicket = () => useTicketTransition('close');
export const useReopenTicket = () => useTicketTransition('reopen');
export const useCancelTicket = () => useTicketTransition('cancel');
